import React, { Component } from 'react';
import FaqSection from './FaqSection';
import './ticketing.css';
import NavBar from './NavBar';
import tk1 from './assets/ticketing/tk1.webp';
import tk2 from './assets/ticketing/tk2.webp';
import tk3 from './assets/ticketing/tk3.webp';
import tk4 from './assets/ticketing/tk4.webp';
import tk5 from './assets/ticketing/tk5.webp';
import tk6 from './assets/ticketing/tk6.webp';
import tk7 from './assets/ticketing/tk7.webp';

interface TicketingProps {}

interface TicketingState {
  lightboxSrc: string | null;
}

class Ticketing extends Component<TicketingProps, TicketingState> {
  constructor(props: TicketingProps) {
    super(props);
    this.state = { lightboxSrc: null };
  }

  openLightbox = (src: string) => {
    this.setState({ lightboxSrc: src });
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null });
  };

  render() {
    return (
      <div className="ticketing-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="ticketing-hero-section">
          <div className="ticketing-hero-video-background">
            <video
              preload="metadata"
              autoPlay
              loop
              muted
              playsInline
              className="ticketing-hero-video"
            >
              <source src={require('./assets/video/web/14366250_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="ticketing-hero-content">
            <div className="ticketing-hero-text">
              <span className="ticketing-hero-eyebrow">pEntities-Ticketing Module</span>
              <h1 className="ticketing-hero-title">
                Enterprise Service Management.<br />Right Inside Microsoft Teams.
              </h1>
              <p className="ticketing-hero-subtitle">
                Jira-level issue tracking structured workflows, SLAs, approvals, sprints and AI automation without asking your teams to leave the tools they already use every day.
              </p>
              <div className="ticketing-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ticketing-hero-cta-btn"
                >
                  Schedule a Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description Section */}
        <section id="overview" className="tk-gendesc-section">
          <div className="ticketing-container">
            <div className="tk-gendesc-content">
              <span className="tk-gendesc-label">General Description</span>
              <h2 className="tk-gendesc-title">One governed service platform across every department</h2>
              <p>
                pEntities Ticketing is an enterprise-grade issue tracking and service management platform built
                natively inside Microsoft Teams. It delivers Jira-level functionality without asking your teams to
                leave the collaboration environment they already use.
              </p>
              <p>
                <b>Ticketing lives where the conversation lives.</b> A single deployment serves IT, HR, Finance,
                Legal and every other department at once each with its own agents, roles and visibility rules.
              </p>
              <p>
                <b>Your tenant, your data.</b> Everything runs inside your own Azure subscription as a Managed
                Application, so your data never leaves your tenant, and the publisher manages the app lifecycle for
                you.
              </p>
              <p>
                <b>All of it is live.</b> Nine work views, a deep ticket data model, an SLA breach engine, a
                no-code Durable-Functions workflow engine, real-time Teams and email notifications, a built-in AI
                (MCP) tool surface, and an integration layer spanning email ingest, webhooks, a REST API and
                Microsoft Planner.
              </p>
            </div>
          </div>
        </section>

        {/* The Ticketing Platform dark */}
        <section id="platform" className="tk-dark-section">
          <div className="ticketing-container">
            <span className="tk-dark-eyebrow">The Ticketing Platform</span>
            <h2 className="tk-dark-title">The depth mature IT and dev teams expect out of the box</h2>
            <p className="tk-dark-lead">
              SLAs, approvals, links, repro fields and worklogs, with nine purpose-built views and a data model
              that handles real enterprise scenarios without months of configuration.
            </p>

            <div className="tk-dark-block">
              <h3>1 · One system, every kind of work</h3>
              <p className="tk-glance">12 issue types · 9 business domains · multi-department, single deployment</p>
              <ul>
                <li><b>Software development</b> Bug, Story, Task, Sub-Task, Epic, Improvement, New Feature.</li>
                <li><b>IT operations</b> Incident, Problem, Change.</li>
                <li><b>Business services</b> Service Request, Question.</li>
                <li><b>Nine domains</b> IT, HR, Finance, Legal, Facilities, Marketing, Operations, Customer Support and Other, each with their own agents, roles and visibility rules.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk1)}>
                <img loading="lazy" decoding="async" src={tk1} alt="One system for every kind of work" />
                <figcaption>Every department runs on one role-isolated platform instead of a patchwork of trackers and shared mailboxes.</figcaption>
              </figure>
            </div>

            <div className="tk-dark-block">
              <h3>2 · A production-grade ticket data model</h3>
              <p className="tk-glance">8 statuses · 6 priorities · 7 resolutions · 14 link types · 22 activity types</p>
              <ul>
                <li><b>Classification</b> 8 statuses (Open through Cancelled), 6 priorities from Trivial to Blocker, 7 resolution outcomes.</li>
                <li><b>Project hierarchy</b> Projects → Epics → Sprints → Tickets → Sub-Tasks, fully linked.</li>
                <li><b>SLA tracking</b> response and resolution deadlines with breach detection, business-hours-aware elapsed time, progress bars and auto-escalation.</li>
                <li><b>Time &amp; bugs</b> story points, estimates, worklog history, plus steps-to-reproduce and browser/OS/build environment.</li>
                <li><b>14 link types &amp; 22 activity types</b> Blocks, Clones, Duplicates, Relates and more, over a full immutable audit trail.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk2)}>
                <img loading="lazy" decoding="async" src={tk2} alt="The ticket data model" />
                <figcaption>A ticket captures the whole story SLAs, approvals, links, repro fields and worklogs so nothing is lost and the full history is one click away.</figcaption>
              </figure>
            </div>

            <div className="tk-dark-block">
              <h3>3 · Nine ways to see the work</h3>
              <p className="tk-glance">Board · List · Backlog · Workload · Roadmap · Reports · Cross-Project · Assets · Calendar</p>
              <ul>
                <li><b>Board (Kanban)</b> drag-and-drop across status columns with real-time write-back.</li>
                <li><b>List &amp; Backlog</b> sortable, filterable table with bulk operations; sprint lanes with velocity tracking.</li>
                <li><b>Workload &amp; Roadmap</b> an assignee heatmap that surfaces overload, and a Gantt timeline for delivery forecasting.</li>
                <li><b>Reports, Cross-Project, Assets &amp; Calendar</b> cumulative-flow and velocity analytics, portfolio views, a CMDB, and a change calendar.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk3)}>
                <img loading="lazy" decoding="async" src={tk3} alt="Nine ways to see the work" />
                <figcaption>Agile delivery, ITIL change management and portfolio oversight in one tool everyone gets the view that fits their job.</figcaption>
              </figure>
            </div>

            <div className="tk-dark-block">
              <h3>4 · The ticket detail panel</h3>
              <p className="tk-glance">Details · Time · Activity · Sub-tasks · Approvals · Related</p>
              <ul>
                <li><b>Details &amp; Time</b> core fields, classification, people and custom fields, plus estimates and the full worklog.</li>
                <li><b>Activity</b> the complete audit timeline, with internal notes visually distinguished from public comments.</li>
                <li><b>Sub-tasks, Approvals &amp; Related</b> child-task progress, the approval pipeline with per-step decisions, and issue links to internal and external systems.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk4)}>
                <img loading="lazy" decoding="async" src={tk4} alt="The ticket detail panel" />
                <figcaption>Complete, auditable context on every ticket everything in one place, so answering "where is this?" takes seconds.</figcaption>
              </figure>
            </div>

            <div className="tk-dark-block">
              <h3>5 · SLA management &amp; auto-escalation</h3>
              <p className="tk-glance">timer engine every 15 min · breach detection · priority auto-escalation</p>
              <ul>
                <li><b>Breach detection</b> an always-on engine monitors every active ticket on a 15-minute timer and flags crossed deadlines.</li>
                <li><b>Priority auto-escalation</b> a breached ticket is bumped one level up the severity ladder automatically.</li>
                <li><b>Audit &amp; fan-out</b> the SLA Monitor appends a system activity, fires a workflow event, and notifies managers, assignee and reporter.</li>
                <li><b>Business-hours aware</b> elapsed time honours a per-store business-hours policy; the same timer drives overdue-approval reminders and recurring tickets.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk5)}>
                <img loading="lazy" decoding="async" src={tk5} alt="SLA management and auto-escalation" />
                <figcaption>SLAs are enforced automatically, not left to memory nothing important goes quiet, and a breach surfaces itself without anyone chasing.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Automation, Governance & Integration white */}
        <section id="automation" className="tk-light-section">
          <div className="ticketing-container">
            <span className="tk-light-eyebrow">Automation, Governance &amp; Integration</span>
            <h2 className="tk-light-title">Automate the routine, govern the access, connect the tools</h2>
            <p className="tk-light-lead">
              A no-code workflow engine, real-time notifications, least-privilege roles, and an open edge that
              fits the systems you already run with AI participating from day one.
            </p>

            <div className="tk-light-block">
              <h3>6 · No-code workflow automation</h3>
              <p className="tk-glance">Durable Functions · 38 trigger types · 19 node types · shipped templates</p>
              <ul>
                <li><b>38 trigger types</b> entity and field changes, schedules, email arrival, Teams, SharePoint, HR/Legal events, finance thresholds and the full ticket lifecycle.</li>
                <li><b>19 node types</b> condition, switch, foreach, do-until, parallel, approval, delay, HTTP, run-workflow and more, with fan-out sub-orchestrations and external-event waits.</li>
                <li><b>Action library</b> create Planner tasks, send and manage email and SharePoint, run analysis and invoke AI-backed actions.</li>
                <li><b>Shipped templates</b> acknowledge new ticket, SLA escalation, incident response, auto-assign by domain, and an email → ticket router.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk6)}>
                <img loading="lazy" decoding="async" src={tk6} alt="No-code workflow automation" />
                <figcaption>Automate triage, escalation and routing without code or a separate automation licence agents spend time resolving, not shuffling tickets.</figcaption>
              </figure>
            </div>

            <div className="tk-light-block">
              <h3>7 · Real-time notifications</h3>
              <p className="tk-glance">Teams Adaptive Cards + email · configurable notification scheme</p>
              <ul>
                <li><b>Two channels</b> Teams Adaptive Cards posted via Microsoft Graph on behalf of the acting user, and email from a system sender.</li>
                <li><b>Event routing</b> created, status, assignment, comment, priority, SLA breach, approvals, resolution and @mentions each reach exactly the right people.</li>
                <li><b>Private agent channel</b> internal notes are suppressed from external notifications and routed only to agents; the acting user is never self-notified.</li>
              </ul>
              <figure className="tk-figure" onClick={() => this.openLightbox(tk7)}>
                <img loading="lazy" decoding="async" src={tk7} alt="Real-time notifications" />
                <figcaption>The right people are told the right things automatically updates arrive in Teams and inbox without keeping a ticket open to know what changed.</figcaption>
              </figure>
            </div>

            <div className="tk-light-block">
              <h3>8 · Governance &amp; role-based access</h3>
              <p className="tk-glance">AAD app roles + per-store roles · domain-scoped permissions</p>
              <ul>
                <li><b>Two layers</b> privileged AAD app roles (HRAdmin, Manager, TicketSupervisor) that see all stores, and per-store roles (Admin, Manager, Agent, Reporter) scoped to specific domains.</li>
                <li><b>Permission grants</b> from create/edit-own for all roles up to delete and role management for Admin only, governed by a permission scheme.</li>
                <li><b>Guardrails</b> reassignment is Manager/Admin-only, explicit per-user denials override grants, and every request validates the caller's token first.</li>
              </ul>
            </div>

            <div className="tk-light-block">
              <h3>9 · Configuration &amp; extensibility</h3>
              <p className="tk-glance">templates · versions · components · custom fields · saved filters · recurring tickets</p>
              <ul>
                <li><b>Issue templates</b> quick-raise forms that pre-fill type, domain, priority and description for common requests.</li>
                <li><b>Custom fields, versions &amp; components</b> extend the schema and manage releases used in classification and filtering.</li>
                <li><b>Saved filters &amp; recurring tickets</b> store the searches a team runs often; daily/weekly/monthly templates the SLA timer instantiates automatically.</li>
              </ul>
            </div>

            <div className="tk-light-block">
              <h3>10 · Integration &amp; AI surface</h3>
              <p className="tk-glance">email ingest · outbound webhooks · REST API keys · Planner · MCP AI tools</p>
              <ul>
                <li><b>Open at the edges</b> inbound email turned into tickets, outbound webhooks on ticket events, scoped REST API keys and a two-way Microsoft Planner bridge.</li>
                <li><b>AI agents via MCP</b> four ticketing tools exposed through a built-in Model Context Protocol server: create, update, reassign and create-from-email with Azure OpenAI classification.</li>
                <li><b>AI from day one</b> tools are gated per model tier, and agents can run as nodes inside the workflow engine for autonomous triage and resolution.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Key differentiators dark */}
        <section id="differentiators" className="tk-diff-section">
          <div className="ticketing-container">
            <span className="tk-diff-eyebrow">Key Differentiators</span>
            <h2 className="tk-diff-title">Why pEntities Ticketing, not another tracker</h2>
            <div className="tk-diff-grid">
              <div className="tk-diff-card">
                <h3>Native Teams experience</h3>
                <p>The full ticketing lifecycle happens where conversations already happen no separate portal.</p>
              </div>
              <div className="tk-diff-card">
                <h3>Zero-infrastructure SaaS</h3>
                <p>Deployed as an Azure Managed Application, so you own your data and control your cost while the publisher manages the app lifecycle.</p>
              </div>
              <div className="tk-diff-card">
                <h3>AI-ready by design</h3>
                <p>MCP tools, workflow nodes and Planner bridging let AI agents participate in ticket resolution from day one.</p>
              </div>
              <div className="tk-diff-card">
                <h3>Enterprise data model</h3>
                <p>SLAs, approvals, 14 link types, repro fields, worklogs, versions and CMDB/DevOps links with no months of configuration.</p>
              </div>
              <div className="tk-diff-card">
                <h3>Multi-domain, single deployment</h3>
                <p>IT, HR, Finance, Legal and more each get a governed, role-isolated view on shared infrastructure.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="ticketing-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">Purpose-built modules for the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              One platform, four specialised modules each with its own domain AI agent, all running inside
              Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Image Lightbox */}
        {this.state.lightboxSrc && (
          <div className="tk-lightbox" onClick={this.closeLightbox} role="dialog" aria-modal="true">
            <button className="tk-lightbox-close" onClick={this.closeLightbox} aria-label="Close">✕</button>
            <img loading="lazy" decoding="async"
              className="tk-lightbox-img"
              src={this.state.lightboxSrc}
              alt="Enlarged screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Footer */}
        <FaqSection path="/solutions/ticketing" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Ticketing;
